import {
  ServiceContainer,
  ServiceEntry,
  ServiceEntryList,
  ServiceGroup,
} from "@/services/backend/qpm/calculations/types/calculation";
import { CalculationServiceEntryType } from "@/services/backend/qpm/calculations/types/calculation-enum";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { CalculationServiceTypeUtil } from "@/services/backend/qpm/calculations/lib/service-type-util";
import { CalculationServiceEntryUtil } from "@/services/backend/qpm/calculations/lib/calculation-service-entry-util";
import { CircleSlash2 } from "lucide-react";
import { Value } from "@/shared/components/domain/qpm/inputs/number-input";
import {
  TimeNumberInput,
  TimeUnit,
} from "@/shared/components/inputs/time-number-input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";

export type ServiceGroupsComponentProps = {
  serviceGroupList: ServiceContainer;
};

export function ServiceGroupComponent({
  serviceGroupList,
}: ServiceGroupsComponentProps) {
  // Order the serviceCostList by serviceTypeKey
  const serviceGroups = [...(serviceGroupList.serviceGroups || [])].sort(
    (a, b) =>
      // Convert serviceTypeKey to string for comparison
      CalculationServiceTypeUtil.toString(a.serviceTypeKey).localeCompare(
        CalculationServiceTypeUtil.toString(b.serviceTypeKey),
      ),
  );

  return (
    <Accordion
      type="multiple"
      defaultValue={[...serviceGroups.map((g) => g.id)]}
    >
      {serviceGroups &&
        serviceGroups.map((serviceGroup, idx) => (
          <AccordionItem value={serviceGroup.id} key={serviceGroup.id}>
            <AccordionTrigger className="bg-secondary rounded-none border-t px-4 font-bold uppercase">
              {t("Servicegruppe")} - {idx + 1}
            </AccordionTrigger>
            <AccordionContent>
              <div className="border-t">
                {serviceGroup && (
                  <div className="">
                    <ServiceEntryCostsParameterComponent
                      serviceGroup={serviceGroup}
                    />
                    <ServiceEntryListComponent
                      serviceEntries={serviceGroup.entries}
                    />
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
    </Accordion>
  );
}

export type ServiceEntryListComponentProps = {
  serviceEntries: ServiceEntryList;
};

export function ServiceEntryListComponent({
  serviceEntries,
}: ServiceEntryListComponentProps) {
  // Define the custom order for CalculationServiceEntryType
  const customOrder = [
    CalculationServiceEntryType.CheckPoint,
    CalculationServiceEntryType.CheckPointVisual,
    CalculationServiceEntryType.CheckPointIoT,
    CalculationServiceEntryType.CheckPointSCB,
    CalculationServiceEntryType.FIV,
    CalculationServiceEntryType.ALoRa,
  ];

  const sortedEntries = [...serviceEntries].sort((a, b) => {
    // First sort by custom order of CalculationServiceEntryType
    const typeComparison =
      customOrder.indexOf(a.type) - customOrder.indexOf(b.type);
    if (typeComparison !== 0) {
      return typeComparison;
    }
    // Then sort by totalAmountOfMeasures ascending
    return a.totalAmountOfMeasures - b.totalAmountOfMeasures;
  });

  return (
    <div className="flex flex-col px-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">{t("Service Art")}</TableHead>
            <TableHead className="text-right">
              {t("Anzahl Servicepunkte")}
            </TableHead>
            <TableHead className="text-right">
              {t("Zusatzkosten pro Kontrollstation in Euro")}
            </TableHead>
            <TableHead className="text-right">
              {t("Preis pro Maßnahme in Euro")}
            </TableHead>
            <TableHead className="text-right">
              {t("Maßnahmen / Erforderliche Maßnahmen")}
            </TableHead>
            <TableHead className="text-right">
              {t("Gesamtpreis in Euro")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedEntries.map((entry) => (
            <ServiceEntryRow key={entry.id} entry={entry} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export type ServiceEntryRowProps = {
  entry: ServiceEntry;
};

export function ServiceEntryRow({ entry }: ServiceEntryRowProps) {
  return (
    <TableRow>
      <TableCell className="text-left font-medium">
        {CalculationServiceEntryUtil.toString(entry.type, entry.serviceTypeKey)}
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={entry.amountOfMonitors} fractions={0} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={entry.additionalCostPerMonitor} fractions={2} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={entry.serviceCostPerMeasure} fractions={2} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={entry.amountOfMeasures} fractions={0} />
        {" / "}
        <Value value={entry.totalAmountOfMeasures} fractions={0} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={entry.totalServiceCost} fractions={2} />
      </TableCell>
    </TableRow>
  );
}

export type ServicePriceListComponentProps = {
  serviceGroup: ServiceGroup;
};

export function ServiceEntryCostsParameterComponent({
  serviceGroup,
}: ServicePriceListComponentProps) {
  return (
    <div className="flex flex-col px-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">
              {t("Gesamtanzahl Kontrollstationen")}
            </TableHead>
            <TableHead className="text-left">
              {t("Anzahl Servicepunkte")}
            </TableHead>
            <TableHead className="text-right">
              <div className="flex items-center justify-end gap-2">
                <CircleSlash2 className="h-4 w-4" />
                {t("Arbeitszeit pro Kontrollstation")}
              </div>
            </TableHead>
            <TableHead className="text-right">
              {t("Zusatzzeit pro Kontrollstation")}
            </TableHead>
            <TableHead className="text-right">
              <div className="flex items-center justify-end gap-2">
                <CircleSlash2 className="h-4 w-4" />
                {t("Wert pro Kontrollstation in Euro")}
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-left font-medium">
              <div className="flex items-center gap-2">
                <Value
                  value={serviceGroup.parameters.totalAmountOfMonitors}
                  fractions={0}
                />
              </div>
            </TableCell>
            <TableCell className="text-left font-medium">
              <Value
                value={serviceGroup.parameters.totalAmountServiceableMonitors}
                fractions={0}
              />
            </TableCell>
            <TableCell className="text-right font-medium">
              <TimeNumberInput
                className="justify-self-end"
                value={serviceGroup.averageServiceTimePerMonitor}
                timeUnit={TimeUnit.Minutes}
                disabled
              />
            </TableCell>
            <TableCell className="text-right font-medium">
              <TimeNumberInput
                className="justify-self-end"
                value={serviceGroup.parameters.additionalServiceTimePerMonitor}
                timeUnit={TimeUnit.Minutes}
                disabled
              />
            </TableCell>
            <TableCell className="text-right font-medium">
              <Value value={serviceGroup.serviceCostPerMonitor} fractions={2} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
